import React, { useState, useEffect } from 'react';
import './MemberStatsModal.css';
import EditTemporaryModal from './EditTemporaryModal';

function MemberStatsModal({ memberId, onClose, onRefresh, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchMemberStats();
  }, [memberId]);

  const fetchMemberStats = async () => {
    try {
      const response = await fetch(`/backend/api/network/member-stats.php?id=${memberId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setMemberData(data.member);
      } else {
        setError(data.error);
      }
    } catch (error) {
      console.error('Error fetching member stats:', error);
      setError('Failed to load member stats');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSaved = () => {
    setShowEditModal(false);
    onRefresh();
    onClose();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this temporary account?')) {
      return;
    }

    try {
      const tempId = memberId.split('_')[1];
      const response = await fetch('/backend/api/network/delete-temporary.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_id: parseInt(tempId) })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        if (onDelete) {
          onDelete(memberId);
        }
        onClose();
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Error deleting temporary account:', error);
      alert('Failed to delete temporary account');
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">{error}</div>
          <button onClick={onClose} className="close-modal-btn">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{memberData.name} {memberData.network_id && `(${memberData.network_id})`}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-content">
          {memberData.type === 'user' ? (
            <>
              <div className="stat-badge">
                <span className="badge-label">Level</span>
                <span className="badge-value">{memberData.level}</span>
              </div>

              {memberData.network_id && (
                <div className="info-row">
                  <span className="info-label">Network ID:</span>
                  <span className="info-value">{memberData.network_id}</span>
                </div>
              )}

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-info">
                    <div className="stat-label">Activity Score</div>
                    <div className="stat-value">{memberData.stats.activity_score}/100</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-info">
                    <div className="stat-label">Network Size</div>
                    <div className="stat-value">{memberData.stats.network_size}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📇</div>
                  <div className="stat-info">
                    <div className="stat-label">Total Contacts</div>
                    <div className="stat-value">{memberData.stats.contacts_total}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">✨</div>
                  <div className="stat-info">
                    <div className="stat-label">Contacts This Week</div>
                    <div className="stat-value">{memberData.stats.contacts_this_week}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-info">
                    <div className="stat-label">Tasks Today</div>
                    <div className="stat-value">{memberData.stats.tasks_completed_today}</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📈</div>
                  <div className="stat-info">
                    <div className="stat-label">Tasks (7 days)</div>
                    <div className="stat-value">{memberData.stats.tasks_completed_7d}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="temp-account-badge">
                <span className="badge-icon">⏳</span>
                <span className="badge-text">Temporary Account</span>
              </div>

              <div className="info-section">
                {memberData.network_id && (
                  <div className="info-row">
                    <span className="info-label">Network ID:</span>
                    <span className="info-value">{memberData.network_id}</span>
                  </div>
                )}
                {memberData.country && (
                  <div className="info-row">
                    <span className="info-label">Country:</span>
                    <span className="info-value">{memberData.country}</span>
                  </div>
                )}
                {memberData.city && (
                  <div className="info-row">
                    <span className="info-label">City:</span>
                    <span className="info-value">{memberData.city}</span>
                  </div>
                )}
                {memberData.notes && (
                  <div className="notes-section">
                    <div className="notes-label">Notes:</div>
                    <div className="notes-content">{memberData.notes}</div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                {memberData.permissions?.can_edit && (
                  <button onClick={handleEdit} className="edit-btn">
                    Edit
                  </button>
                )}
                {memberData.permissions?.can_delete && (
                  <button onClick={handleDelete} className="delete-btn">
                    Delete Temporary Account
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {showEditModal && memberData && (
          <EditTemporaryModal
            tempData={{
              id: memberId.split('_')[1],
              first_name: memberData.name.split(' ')[0],
              last_name: memberData.name.split(' ').slice(1).join(' '),
              network_id: memberData.network_id,
              country: memberData.country,
              city: memberData.city,
              notes: memberData.notes
            }}
            onClose={() => setShowEditModal(false)}
            onSaved={handleEditSaved}
          />
        )}
      </div>
    </div>
  );
}

export default MemberStatsModal;

